import { HospitalInfoResponseDto } from "@/data/dto/hospital-info-reponse-dto";
import { AddHospitalRequestPayload } from "@/data/payload/admin/add-hospital-request-payload";
import { DeleteHospitalRequestPayload } from "@/data/payload/admin/delete-hospital-request-payload";
import { HospitalInfoRequestPayload } from "@/data/payload/hospital-info-request-payload";
import {
  isRecord,
  normalizeArrayResponse,
  normalizeObjectResponse,
} from "@/data/source/response-normalizer";
import { URLList } from "@/data/url_list";
import { createLogger, withApiLogging } from "@/utils/logger/logger";
import { FetchApiClientMethod } from "@sadtx/fetch-api-client";
import { errorConverter, SATError, SATResult } from "@sadtx/sat-error";

export class HospitalSource {
  private readonly logger = createLogger("HospitalSource");

  constructor(private readonly client: FetchApiClientMethod) {}

  async getHospitalInfo(
    payload: HospitalInfoRequestPayload,
  ): Promise<SATResult<HospitalInfoResponseDto, SATError>> {
    const url = `${URLList.HOSPITAL.GET_HOSPITAL_INFO}/${payload.hospitalCode}`;
    try {
      const data = await withApiLogging(this.logger, { method: "GET", url }, async () => {
        const response = await this.client.get(url);
        return response.data;
      });
      return SATResult.success(
        normalizeObjectResponse(
          data,
          HOSPITAL_DETAIL_RESPONSE_KEYS,
          isHospitalInfoCandidate,
        ) as HospitalInfoResponseDto,
      );
    } catch (error: unknown) {
      return SATResult.failure(errorConverter.convertError(error));
    }
  }

  async addHospitalInfo(payload: AddHospitalRequestPayload): Promise<SATResult<void, SATError>> {
    const url = URLList.HOSPITAL.POST_ADD_HOSPITAL;
    try {
      await withApiLogging(this.logger, { method: "POST", url, payload }, async () => {
        const response = await this.client.post(url, payload);
        return response.data;
      });
      return SATResult.success(void 0);
    } catch (error: unknown) {
      return SATResult.failure(errorConverter.convertError(error));
    }
  }

  async getHospitalList(): Promise<SATResult<HospitalInfoResponseDto[], SATError>> {
    const url = URLList.HOSPITAL.GET_HOSPITAL_LIST;
    try {
      const data = await withApiLogging(this.logger, { method: "GET", url }, async () => {
        const response = await this.client.get(url);
        return response.data;
      });
      return SATResult.success(
        normalizeArrayResponse(data, HOSPITAL_LIST_RESPONSE_KEYS) as HospitalInfoResponseDto[],
      );
    } catch (error: unknown) {
      return SATResult.failure(errorConverter.convertError(error));
    }
  }

  async deleteHospitalInfo(
    payload: DeleteHospitalRequestPayload,
  ): Promise<SATResult<void, SATError>> {
    const url = `${URLList.HOSPITAL.DELETE_HOSPITAL}/${payload.hospitalCode}`;
    try {
      await withApiLogging(this.logger, { method: "DELETE", url }, async () => {
        const response = await this.client.delete(url);
        return response.data;
      });
      return SATResult.success(void 0);
    } catch (error: unknown) {
      return SATResult.failure(errorConverter.convertError(error));
    }
  }
}

const HOSPITAL_LIST_RESPONSE_KEYS = [
  "hospitalList",
  "hospitals",
  "dataList",
  "items",
  "list",
  "result",
  "content",
  "data",
] as const;

const HOSPITAL_DETAIL_RESPONSE_KEYS = [
  "hospital",
  "hospitalInfo",
  "result",
  "content",
  "data",
] as const;

function isHospitalInfoCandidate(value: unknown): boolean {
  return (
    isRecord(value) &&
    typeof value.hospitalName === "string" &&
    typeof value.hospitalCode === "string"
  );
}
